import { clsx } from "clsx";
import type React from "react";
import type { ComponentPropsWithRef, ElementType, ReactNode } from "react";
import { createElement } from "react";
import {
  paddingBreakpointMap,
  paddingMap,
  paddingXBreakpointMap,
  paddingXMap,
  paddingYBreakpointMap,
  paddingYMap,
  type Radius,
  type ResponsiveSpace,
  radiusMap,
  resolveResponsive,
} from "../tokens";

export type BoxElement = keyof React.JSX.IntrinsicElements;

type BoxOwnProps = {
  padding?: ResponsiveSpace;
  paddingX?: ResponsiveSpace;
  paddingY?: ResponsiveSpace;
  radius?: Radius;
  children?: ReactNode;
  className?: string;
};

export type BoxProps<T extends BoxElement = "div"> = BoxOwnProps & { as?: T } & Omit<
    ComponentPropsWithRef<T>,
    keyof BoxOwnProps | "as"
  >;

export function Box<T extends BoxElement = "div">({
  as,
  padding,
  paddingX,
  paddingY,
  radius,
  className,
  children,
  ...rest
}: BoxProps<T>) {
  const Component = (as ?? "div") as ElementType;
  const classes = clsx(
    paddingX !== undefined && resolveResponsive(paddingX, paddingXMap, paddingXBreakpointMap),
    paddingY !== undefined && resolveResponsive(paddingY, paddingYMap, paddingYBreakpointMap),
    paddingX === undefined &&
      paddingY === undefined &&
      padding !== undefined &&
      resolveResponsive(padding, paddingMap, paddingBreakpointMap),
    radius !== undefined && radiusMap[radius],
    className,
  );

  return createElement(
    Component,
    {
      "data-component": "Box",
      ...rest,
      className: classes || undefined,
    },
    children,
  );
}

Box.displayName = "Box";
